import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { IMAGES, CRISIS_STATS, COMPARISON } from "@/data";
import WhaleShark from "@/components/WhaleShark";
import ChapterMark from "@/components/ChapterMark";

export default function Crisis() {
    return (
        <section id="crisis" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32" data-testid="crisis-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.06]"
                style={{ backgroundImage: "url(/images/harbour-beach-vaucluse.jpg)" }}
            />
            <WhaleShark
                className="right-[-150px] top-20 w-[480px] rotate-6"
                opacity={0.1}
                testid="crisis-whale"
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="01" label="The Issue" testid="crisis-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="crisis-heading">
                        A heavily contaminated river, and cleanup efforts that barely scratch the surface
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="crisis-intro">
                        For over 750,000 people living along the Parramatta River catchment, including many
                        local Indigenous communities with deep cultural ties to these waterways, clean, safe
                        access to the river is not a luxury. A lack of business initiative and proactive
                        problem-solving has left current cleanup efforts minor and ineffective in the long term.
                    </p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 relative rounded-xl overflow-hidden border border-[#12283C]"
                    data-testid="crisis-image-card"
                >
                    <img
                        src={IMAGES.foreshore}
                        alt="Littered Parramatta River foreshore with the city skyline behind"
                        className="w-full h-[300px] md:h-[380px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/20 to-transparent" />
                    <div className="absolute bottom-0 p-6 max-w-2xl">
                        <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">Photographed on the river</p>
                        <p className="mt-2 text-base text-slate-300 leading-relaxed">
                            A Parramatta River foreshore after the tide: plastic bottles and debris deposited
                            within sight of the city skyline.
                        </p>
                    </div>
                </motion.div>

                <div className="mt-14 border-t border-[#12283C]" data-testid="crisis-stats-list">
                    {CRISIS_STATS.map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.45, delay: i * 0.05 }}
                            className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-2 sm:gap-10 py-7 border-b border-[#12283C] transition-transform duration-300 hover:translate-x-2"
                            data-testid={`crisis-stat-row-${i}`}
                        >
                            <div>
                                <p className="font-display font-semibold text-3xl md:text-4xl text-sky-200">{stat.value}</p>
                                <p className="mt-1 text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">{stat.label}</p>
                            </div>
                            <p className="text-base md:text-lg text-slate-400 leading-relaxed self-center">{stat.detail}</p>
                        </motion.div>
                    ))}
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="crisis-photo-pair">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-xl overflow-hidden border border-[#12283C]"
                        data-testid="crisis-truck-banner"
                    >
                        <img
                            src={IMAGES.truck}
                            alt="A water recycler truck pumping at the edge of a catchment waterway"
                            className="w-full h-[300px] md:h-[340px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/20 to-transparent" />
                        <div className="absolute bottom-0 p-6">
                            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">The current response</p>
                            <p className="mt-2 text-base text-slate-300 leading-relaxed">
                                Diesel trucks, pumps and occasional manual clean-ups: expensive, episodic, and
                                gone by the next tide.
                            </p>
                        </div>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="relative rounded-xl overflow-hidden border border-[#12283C]"
                        data-testid="crisis-canal-banner"
                    >
                        <img
                            src="/images/canal-cyclist.webp"
                            alt="A cyclist looks over a polluted catchment canal after heavy rain"
                            className="w-full h-[300px] md:h-[340px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#020D18] via-[#020D18]/20 to-transparent" />
                        <div className="absolute bottom-0 p-6">
                            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">After the rain</p>
                            <p className="mt-2 text-base text-slate-300 leading-relaxed">
                                Every storm carries street litter straight into the catchment, photographed on
                                the river system, Sydney.
                            </p>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-16" data-testid="comparison-block">
                    <h3 className="font-display font-semibold text-3xl" data-testid="comparison-heading">
                        Parramatta River vs Sydney Harbour, the data side by side
                    </h3>
                    <div className="mt-6 overflow-x-auto border border-[#12283C] rounded-xl" data-testid="comparison-table-wrapper">
                        <table className="w-full text-base" data-testid="comparison-table">
                            <thead>
                                <tr className="bg-[#06182A] text-left">
                                    <th className="p-4 font-semibold text-slate-400 w-1/5">Measure</th>
                                    <th className="p-4 font-semibold text-sky-200 w-2/5">Parramatta River</th>
                                    <th className="p-4 font-semibold text-slate-400 w-2/5">Sydney Harbour</th>
                                </tr>
                            </thead>
                            <tbody>
                                {COMPARISON.map((row, i) => (
                                    <tr key={row.metric} className={i % 2 === 0 ? "bg-[#020D18]" : "bg-[#041222]"} data-testid={`comparison-row-${i}`}>
                                        <td className="p-4 font-semibold text-slate-200 align-top">{row.metric}</td>
                                        <td className="p-4 text-slate-300 align-top">{row.parramatta}</td>
                                        <td className="p-4 text-slate-400 align-top">{row.harbour}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-4 flex items-start gap-3 text-sm text-slate-500 leading-relaxed" data-testid="comparison-footnote">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-sky-200" />
                        <p>
                            Sources: NSW EPA Broadscale Microplastic Assessment 2021–2024 (published March 2026);
                            Sydney Water clean-up operations 2024; NSW EPA litter monitoring 2024–25; NSW Health.
                            For the record: the EPA's #10 ranking is Middle Harbour Creek, and NSW does not
                            publish a single harbour-wide pollution score.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
